import { DebtAllocationSnapshot, MonthlyProjectionRow, PropertyModelEvent, ProjectionEvent, PropertyRiskProfile } from '../types';
import { buildAmortizationSchedule, buildAmortizationScheduleFromDebtSnapshot } from './buildAmortizationSchedule';
import { applyEventSchedule } from './applyEventSchedule';
import { FinancialInputs } from '../types';
import { derivePropertyHoldPeriodYears } from './debt';

const startOfProjection = () => new Date();

const addMonths = (date: Date, months: number) => {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
};

const formatPeriod = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

const getMonthlyEvents = (events: ProjectionEvent[], month: number) => events.filter((event) => event.month === month);

export const buildMonthlyProjection = (
  inputs: FinancialInputs,
  riskProfile: PropertyRiskProfile,
  debtSnapshot?: DebtAllocationSnapshot | null,
  propertyEvents: PropertyModelEvent[] = []
): MonthlyProjectionRow[] => {
  const propertyReserves = inputs.propertyReserves ?? 0;
  const portfolioReservesAllocated = inputs.portfolioReservesAllocated ?? 0;
  const annualCapexReserve = inputs.annualCapexReserve ?? 0;
  const reservePercentOfRent = inputs.reservePercentOfRent ?? 0;
  const derivedHoldYears = derivePropertyHoldPeriodYears(debtSnapshot);
  const holdMonths = Math.max(24, inputs.projectionMonths || 24, derivedHoldYears * 12);
  const amortization = debtSnapshot
    ? buildAmortizationScheduleFromDebtSnapshot(debtSnapshot)
    : buildAmortizationSchedule(inputs);
  const projectionStart = startOfProjection();
  const monthlyEvents = applyEventSchedule(inputs, riskProfile, holdMonths, propertyEvents);

  let monthlyRent = inputs.annualRent / 12;
  let taxesMonthly = inputs.annualTaxes / 12;
  let insuranceMonthly = inputs.annualInsurance / 12;
  let maintenanceMonthly = inputs.annualMaintenance / 12;
  let hoaMonthly = inputs.annualHoa / 12;
  let reserveBalance = propertyReserves + portfolioReservesAllocated;
  let cumulativeCashFlow = 0;
  let cumulativePrincipalPaid = 0;
  let estimatedPropertyValue = inputs.purchasePrice;

  return Array.from({ length: holdMonths }, (_, index) => {
    const month = index + 1;
    if (month > 1 && (month - 1) % 12 === 0) {
      monthlyRent *= (1 + (inputs.rentGrowth / 100));
      taxesMonthly *= (1 + (inputs.expenseGrowth / 100));
      insuranceMonthly *= (1 + (inputs.expenseGrowth / 100));
      maintenanceMonthly *= (1 + (inputs.expenseGrowth / 100));
      hoaMonthly *= (1 + (inputs.expenseGrowth / 100));
    }

    const scheduleRow = amortization[Math.min(index, amortization.length - 1)];
    const periodDate = addMonths(projectionStart, index);
    const monthEvents = getMonthlyEvents(monthlyEvents, month);

    const smoothedVacancy = inputs.vacancyMode === 'smoothed' ? monthlyRent * ((inputs.vacancyRate / 100) / 12) : 0;
    const scheduledVacancyLoss = 0;
    const vacancyEventExpense = monthEvents
      .filter((event) => event.category === 'vacancy')
      .reduce((sum, event) => sum + event.amount, 0);
    const vacancyLoss = smoothedVacancy + scheduledVacancyLoss + vacancyEventExpense;

    const effectiveRentalIncome = Math.max(0, monthlyRent - vacancyLoss);
    const otherIncome = inputs.otherIncomeMonthly || 0;
    const totalIncome = effectiveRentalIncome + otherIncome;
    const propertyManagement = totalIncome * ((inputs.propertyManagementRate || 0) / 100);
    // Reserve funding is modeled as an explicit cash transfer, not an operating expense.
    // That keeps NOI clean while still reducing spendable cash flow when reserves are funded.
    const repairReserveMonthly = inputs.reserveFundingMode === 'percent'
      ? monthlyRent * (reservePercentOfRent / 100)
      : inputs.reserveFundingMode === 'off'
        ? 0
        : annualCapexReserve / 12;
    // Keep the reserve bucket intuitive: no automatic monthly turnover reserve unless a future
    // explicit funding control is added. Turnover costs still hit as reserve draws when they occur.
    const turnoverReserve = 0;
    const utilities = inputs.utilitiesMonthly || 0;

    const capexEvents = monthEvents
      .filter((event) => event.category === 'capex')
      .reduce((sum, event) => sum + event.amount, 0);
    const makeReadyCosts = monthEvents
      .filter((event) => event.category === 'makeReady')
      .reduce((sum, event) => sum + event.amount, 0);
    const turnoverCosts = monthEvents
      .filter((event) => event.category === 'turnover')
      .reduce((sum, event) => sum + event.amount, 0);
    const maintenanceEvents = monthEvents
      .filter((event) => event.category === 'maintenance')
      .reduce((sum, event) => sum + event.amount, 0);
    const otherEventIncome = monthEvents
      .filter((event) => event.impactType === 'income')
      .reduce((sum, event) => sum + event.amount, 0);
    const otherEventExpense = monthEvents
      .filter((event) => event.category === 'expense')
      .reduce((sum, event) => sum + event.amount, 0);

    const applianceReplacementCosts = monthEvents
      .filter((event) => event.label.toLowerCase().includes('fridge') || event.label.toLowerCase().includes('appliance'))
      .reduce((sum, event) => sum + event.amount, 0);
    const paintFlooringCosts = monthEvents
      .filter((event) => event.label.toLowerCase().includes('paint') || event.label.toLowerCase().includes('floor'))
      .reduce((sum, event) => sum + event.amount, 0);

    const operatingExpenseTotal = taxesMonthly + insuranceMonthly + hoaMonthly + propertyManagement + maintenanceMonthly + utilities;
    const monthlyNoi = totalIncome - operatingExpenseTotal;
    // Reserve balance is a pure cash bucket: prior balance + contribution - actual draw.
    const reserveContribution = repairReserveMonthly + turnoverReserve;
    const reserveDraw = capexEvents + makeReadyCosts + turnoverCosts + maintenanceEvents + otherEventExpense;
    // This is the cash generated after operations, debt service, and actual event spending,
    // but before any optional reserve funding transfer for the month.
    const operatingCashFlowBeforeReserves =
      totalIncome +
      otherEventIncome -
      operatingExpenseTotal -
      scheduleRow.payment -
      reserveDraw;
    // Cash flow is the actual spendable money left after the full mortgage payment clears.
    const endingCashFlow = operatingCashFlowBeforeReserves - reserveContribution;
    const monthlyAppreciation = estimatedPropertyValue * ((inputs.appreciationRate || 0) / 100 / 12);
    // True return includes cash flow plus equity created from amortization and appreciation.
    const monthlyTrueReturn = endingCashFlow + scheduleRow.principal + monthlyAppreciation;

    cumulativeCashFlow += endingCashFlow;
    cumulativePrincipalPaid += scheduleRow.principal;
    reserveBalance = Math.max(0, reserveBalance + reserveContribution - reserveDraw);
    estimatedPropertyValue += monthlyAppreciation;

    return {
      month,
      monthLabel: `Month ${month}`,
      periodStart: formatPeriod(periodDate),
      beginningLoanBalance: scheduleRow.beginningBalance,
      monthlyNoi,
      grossScheduledRent: monthlyRent,
      vacancyLoss,
      effectiveRentalIncome,
      otherIncome,
      totalIncome,
      propertyTaxes: taxesMonthly,
      insurance: insuranceMonthly,
      hoa: hoaMonthly,
      propertyManagement,
      routineMaintenance: maintenanceMonthly,
      operatingCashFlowBeforeReserves,
      cashFlowBeforeReserveFunding: operatingCashFlowBeforeReserves,
      repairReserve: repairReserveMonthly,
      turnoverReserve,
      reserveFundingLabel: inputs.reserveFundingMode === 'percent' ? 'Percent of Rent Reserve' : inputs.reserveFundingMode === 'off' ? 'Reserve Funding Off' : 'Fixed Annual Reserve',
      reserveFundingBase: inputs.reserveFundingMode === 'percent' ? monthlyRent : annualCapexReserve,
      utilities,
      scheduledDebtService: scheduleRow.payment,
      interestPaid: scheduleRow.interest,
      principalPaid: scheduleRow.principal,
      totalDebtService: scheduleRow.payment,
      monthlyAppreciation,
      monthlyTrueReturn,
      capexEvents,
      makeReadyCosts,
      applianceReplacementCosts,
      paintFlooringCosts,
      otherEventIncome,
      otherEventExpense: otherEventExpense + maintenanceEvents + turnoverCosts,
      endingCashFlow,
      cashFlowAfterReserveFunding: endingCashFlow,
      cumulativeCashFlow,
      reserveContribution,
      reserveDraw,
      endingReserveBalance: reserveBalance,
      endingLoanBalance: scheduleRow.endingBalance,
      remainingLoanBalance: scheduleRow.endingBalance,
      cumulativePrincipalPaid
    };
  });
};
